import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Building2,
  MapPin,
  Search,
  Download,
  Plus,
  Edit,
  Trash2,
  Users,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  X,
} from 'lucide-react';
import { LoadingState } from '../../components/AsyncStates';
import { useDialogError, DialogErrorAlert } from '../../components/ui/DialogError';
import { getBranches, getDepartments, addUpdateBranches, addUpdateDepartments, deleteBranches, deleteDepartments } from '../../api/General/Company';
import type { Branch, Department, BranchInput, DepartmentInput } from '../../models/general';
import { useWriteAccess } from '../../hooks/useWriteAccess';
import RestrictedWriteNotice, { RestrictedWriteHint } from '../../components/subscription/RestrictedWriteNotice';
import { getApiErrorMessage } from '../../utils/apiErrors';

// ---- Main Component ----
export default function DepartmentsLocations() {
  const { t } = useTranslation();
  // CO-24 — /api/Company (branches + departments) is NOT allowlisted: these writes 403 while the
  // tenant is Restricted/Dormant. Export stays enabled — BR-29, never blocked at any access state.
  const write = useWriteAccess();
  const companyId = useSelector((state: { user?: { company?: { id?: number } } }) => state.user?.company?.id);
  const [activeTab, setActiveTab] = useState<'departments' | 'locations'>('departments');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDept, setSelectedDept] = useState<Department | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [saving, setSaving] = useState(false);

  // CO-49: a refused write must explain itself INSIDE the dialog, not only in a
  // toast that auto-dismisses. One error surface per dialog.
  const formError = useDialogError();
  const deleteErrorState = useDialogError();

  // Data state
  const [departments, setDepartments] = useState<Department[]>([]);
  const [locations, setLocations] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formParentId, setFormParentId] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const [deptResult, branchResult] = await Promise.all([
        getDepartments(companyId),
        getBranches(companyId),
      ]);

      if (deptResult.errors.length === 0) {
        setDepartments(deptResult.payload);
      } else {
        setError(getApiErrorMessage(deptResult, t));
      }

      if (branchResult.errors.length === 0) {
        setLocations(branchResult.payload);
      } else {
        setError(getApiErrorMessage(branchResult, t));
      }
    } catch (err) {
      setError(getApiErrorMessage(err, t, t('Failed to load data')));
    } finally {
      setLoading(false);
    }
  }, [companyId, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Filter and sort
  const filteredDepartments = departments
    .filter((dept) => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        dept.name?.toLowerCase().includes(term) ||
        dept.parentDepartmentName?.toLowerCase().includes(term)
      );
    })
    .sort((a, b) => {
      let aVal = '';
      let bVal = '';
      if (sortField === 'name') { aVal = a.name; bVal = b.name; }
      else if (sortField === 'parentDepartment') { aVal = a.parentDepartmentName || ''; bVal = b.parentDepartmentName || ''; }
      else if (sortField === 'employeeCount') { return sortDirection === 'asc' ? (a.employeeCount || 0) - (b.employeeCount || 0) : (b.employeeCount || 0) - (a.employeeCount || 0); }
      const comparison = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  const filteredLocations = locations
    .filter((loc) => {
      if (!searchTerm) return true;
      return loc.name?.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      const comparison = (a.name || '').localeCompare(b.name || '');
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  // CO-49: the Export control used to render with no handler at all. The page
  // already holds every row it would export, so it exports exactly what is on
  // screen: the active tab, the current search filter, and the current sort.
  const handleExport = () => {
    const escapeCell = (value: unknown) => {
      const s = value == null ? '' : String(value);
      // Guard against spreadsheet formula injection on =,+,-,@ leading cells.
      const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
      return `"${safe.replace(/"/g, '""')}"`;
    };

    const rows: string[][] = activeTab === 'departments'
      ? [
          [t('Department Name'), t('Parent Department'), t('Employee Count'), t('Sub-departments')],
          ...filteredDepartments.map((d) => [
            d.name ?? '',
            d.parentDepartmentName ?? '',
            String(d.employeeCount ?? 0),
            String(d.subDepartmentCount ?? 0),
          ]),
        ]
      : [
          [t('Branch Name')],
          ...filteredLocations.map((l) => [l.name ?? '']),
        ];

    // BOM so Excel reads UTF-8 (Arabic department names) instead of mojibake.
    const csv = '﻿' + rows.map((r) => r.map(escapeCell).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `${activeTab === 'departments' ? 'departments' : 'locations'}-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleAdd = () => {
    setFormName('');
    setFormParentId(null);
    setSelectedDept(null);
    setSelectedBranch(null);
    formError.clear();
    setShowAddModal(true);
  };

  const handleEditDept = (dept: Department) => {
    setSelectedDept(dept);
    setSelectedBranch(null);
    setFormName(dept.name);
    setFormParentId(dept.parentDepartmentId ?? null);
    formError.clear();
    setShowEditModal(true);
  };

  const handleEditBranch = (branch: Branch) => {
    setSelectedBranch(branch);
    setSelectedDept(null);
    setFormName(branch.name);
    formError.clear();
    setShowEditModal(true);
  };

  const handleDeleteDept = (dept: Department) => {
    setSelectedDept(dept);
    setSelectedBranch(null);
    deleteErrorState.clear();
    setShowDeleteModal(true);
  };

  const handleDeleteBranch = (branch: Branch) => {
    setSelectedBranch(branch);
    setSelectedDept(null);
    deleteErrorState.clear();
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setSaving(true);
    deleteErrorState.clear();
    try {
      if (activeTab === 'departments' && selectedDept) {
        const result = await deleteDepartments([selectedDept.id]);
        if (result.errors.length > 0) {
          deleteErrorState.capture(result, t('Delete failed'));
          setSaving(false);
          return;
        }
      } else if (selectedBranch) {
        const result = await deleteBranches([selectedBranch.id]);
        if (result.errors.length > 0) {
          deleteErrorState.capture(result, t('Delete failed'));
          setSaving(false);
          return;
        }
      }
      setShowDeleteModal(false);
      setSelectedDept(null);
      setSelectedBranch(null);
      await fetchData();
    } catch (err) {
      // The dialog stays open and now says why. The interceptor's toast is the
      // peripheral signal, never the only one.
      deleteErrorState.capture(err, t('Delete failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) return;
    setSaving(true);
    formError.clear();
    try {
      if (activeTab === 'departments') {
        const input: DepartmentInput[] = [{
          id: showEditModal && selectedDept ? selectedDept.id : 0,
          name: formName.trim(),
          parentDepartmentId: formParentId,
        }];
        const result = await addUpdateDepartments(input);
        if (result.errors.length > 0) {
          formError.capture(result, t('Save failed'));
          setSaving(false);
          return;
        }
      } else {
        const input: BranchInput[] = [{
          id: showEditModal && selectedBranch ? selectedBranch.id : 0,
          name: formName.trim(),
        }];
        const result = await addUpdateBranches(input);
        if (result.errors.length > 0) {
          formError.capture(result, t('Save failed'));
          setSaving(false);
          return;
        }
      }
      closeModals();
      await fetchData();
    } catch (err) {
      formError.capture(err, t('Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedDept(null);
    setSelectedBranch(null);
    formError.clear();
    deleteErrorState.clear();
  };

  const SortableHeader = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <th
      className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-white transition-colors"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        <span>{children}</span>
        {sortField === field ? (
          sortDirection === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-green-600" /> : <ChevronDown className="w-3.5 h-3.5 text-green-600" />
        ) : (
          <ChevronsUpDown className="w-3.5 h-3.5 text-gray-700" />
        )}
      </div>
    </th>
  );

  // ---- Departments Table ----
  const DepartmentsTable = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <SortableHeader field="name">{t('Department Name')}</SortableHeader>
            <SortableHeader field="parentDepartment">{t('Parent Department')}</SortableHeader>
            <SortableHeader field="employeeCount">{t('Employee Count')}</SortableHeader>
            <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">{t('Sub-departments')}</th>
            <th className="px-6 py-3 text-end text-xs font-medium text-gray-500 uppercase tracking-wider">{t('Actions')}</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filteredDepartments.map((department) => (
            <tr key={department.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">{department.name}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {department.parentDepartmentName || '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-gray-700" />
                  <span className="text-sm text-gray-900">{department.employeeCount ?? 0}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {department.subDepartmentCount ?? 0}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-end text-sm font-medium">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => handleEditDept(department)}
                    {...write.buttonProps}
                    className={`text-blue-600 hover:text-blue-900 transition-colors ${write.disabledClassName}`}
                    data-testid={`edit-dept-${department.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteDept(department)}
                    {...write.buttonProps}
                    className={`text-red-600 hover:text-red-900 transition-colors ${write.disabledClassName}`}
                    data-testid={`delete-dept-${department.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ---- Locations Table ----
  const LocationsTable = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <SortableHeader field="name">{t('Branch Name')}</SortableHeader>
            <th className="px-6 py-3 text-end text-xs font-medium text-gray-500 uppercase tracking-wider">{t('Actions')}</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filteredLocations.map((branch) => (
            <tr key={branch.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-700" />
                  <span className="text-sm font-medium text-gray-900">{branch.name}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-end text-sm font-medium">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => handleEditBranch(branch)}
                    {...write.buttonProps}
                    className={`text-blue-600 hover:text-blue-900 transition-colors ${write.disabledClassName}`}
                    data-testid={`edit-branch-${branch.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteBranch(branch)}
                    {...write.buttonProps}
                    className={`text-red-600 hover:text-red-900 transition-colors ${write.disabledClassName}`}
                    data-testid={`delete-branch-${branch.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ---- Add/Edit Modal ----
  // 🔴 CO-316 — this is a RENDER FUNCTION, and it must be INVOKED (`{AddEditModal({...})}`), never
  // mounted as an element (`<AddEditModal />`). It closes over the parent's state, so React sees a NEW
  // component type on every parent render; mounting it as an element made React unmount and remount the
  // whole modal on each keystroke, and the name input lost focus after every character. Calling it inlines
  // the JSX into the parent's tree instead, so the input keeps its identity and its focus.
  const AddEditModal = ({ isEdit = false }: { isEdit?: boolean }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="dept-loc-modal-title">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 id="dept-loc-modal-title" className="text-xl font-semibold text-gray-900">
              {isEdit ? t('Edit') : t('Add')} {activeTab === 'departments' ? t('Department') : t('Location')}
            </h2>
            <button
              onClick={closeModals}
              disabled={saving}
              aria-label={t('Close')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:pointer-events-none"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <DialogErrorAlert error={formError.error} onDismiss={formError.clear} className="mb-0" />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {activeTab === 'departments' ? t('Department Name') : t('Branch Name')} *
              </label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors"
                placeholder={activeTab === 'departments' ? t('Enter department name') : t('Enter branch name')}
                required
                data-testid="form-name-input"
              />
            </div>

            {activeTab === 'departments' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Parent Department')}
                </label>
                <select
                  value={formParentId ?? ''}
                  onChange={(e) => setFormParentId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors"
                  data-testid="form-parent-select"
                >
                  <option value="">{t('Select parent department')}</option>
                  {departments
                    .filter(d => !selectedDept || d.id !== selectedDept.id)
                    .map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                </select>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <button
                type="button"
                onClick={closeModals}
                disabled={saving}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                {t('Cancel')}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                data-testid="form-submit-btn"
              >
                {saving ? t('Saving...') : isEdit ? t('Update') : t('Create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  // ---- Delete Confirmation Modal ----
  const DeleteModal = () => {
    const itemName = activeTab === 'departments'
      ? selectedDept?.name
      : selectedBranch?.name;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">{t('Confirm Deletion')}</h2>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={saving}
                aria-label={t('Close')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:pointer-events-none"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 mb-4">
              {t('Are you sure you want to delete the')} {activeTab === 'departments' ? t('department') : t('location')}
              <span className="font-semibold"> &quot;{itemName}&quot;</span>?
            </p>

            <DialogErrorAlert error={deleteErrorState.error} onDismiss={deleteErrorState.clear} />

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={saving}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={confirmDelete}
                disabled={saving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                data-testid="confirm-delete-btn"
              >
                {saving ? t('Deleting...') : t('Delete')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <LoadingState title={t('Loading...')} size="md" />;
  }

  if (error && departments.length === 0 && locations.length === 0) {
    return (
      <div className="p-6 max-w-7xl mx-auto text-center">
        <p className="text-red-500">{error}</p>
        <button onClick={fetchData} className="mt-4 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors">
          {t('Retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('Departments & Locations')}</h1>
            <p className="text-gray-600 mt-2">{t('Manage organizational departments and office locations')}</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Building2 className="w-4 h-4" />
            <span>
              {activeTab === 'departments' ? departments.length : locations.length} {t(activeTab)}
            </span>
          </div>
        </div>
      </div>

      <RestrictedWriteNotice className="mb-6" />

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-border">
          <nav className="-mb-px flex gap-8">
            <button
              onClick={() => { setActiveTab('departments'); setSearchTerm(''); }}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'departments' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-border'
              }`}
              data-testid="tab-departments"
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                <span>{t('Departments')} ({departments.length})</span>
              </div>
            </button>
            <button
              onClick={() => { setActiveTab('locations'); setSearchTerm(''); }}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'locations' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-border'
              }`}
              data-testid="tab-locations"
            >
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>{t('Locations')} ({locations.length})</span>
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute start-3 top-1/2 transform -translate-y-1/2 text-gray-700" />
            <input
              type="text"
              placeholder={t(`Search ${activeTab}...`)}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ps-10 pe-4 py-2 border border-border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors w-80"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleExport}
              disabled={(activeTab === 'departments' ? filteredDepartments : filteredLocations).length === 0}
              title={t('Export the rows currently shown as CSV')}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-border text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:pointer-events-none"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4" />
              <span>{t('Export CSV')}</span>
            </button>
            <button
              onClick={handleAdd}
              {...write.buttonProps}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-green-500"
              data-testid="add-btn"
            >
              <Plus className="w-4 h-4" />
              <span>{t('Add')} {activeTab === 'departments' ? t('Department') : t('Location')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* CO-24 repair — row Edit/Delete sit far below the page notice on a long table, and a
          disabled control cannot show a tooltip. State the reason where the dead icons are. */}
      <RestrictedWriteHint className="mb-2" />

      {/* Data Table */}
      <div className="bg-card rounded-lg shadow-sm border border-border" data-testid="data-table">
        {(activeTab === 'departments' ? filteredDepartments : filteredLocations).length === 0 ? (
          <div className="text-center py-12">
            {activeTab === 'departments' ? (
              <Building2 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            ) : (
              <MapPin className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            )}
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('No')} {t(activeTab)} {t('found')}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchTerm ? t('Try adjusting your search criteria.') : t(`Get started by adding your first ${activeTab === 'departments' ? 'department' : 'location'}.`)}
            </p>
            {!searchTerm && (
              <button
                onClick={handleAdd}
                {...write.buttonProps}
                className="inline-flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-green-500"
              >
                <Plus className="w-4 h-4 me-2" />
                {t('Add')} {activeTab === 'departments' ? t('Department') : t('Location')}
              </button>
            )}
          </div>
        ) : activeTab === 'departments' ? (
          <DepartmentsTable />
        ) : (
          <LocationsTable />
        )}
      </div>

      {/* Modals */}
      {showAddModal && AddEditModal({ isEdit: false })}
      {showEditModal && AddEditModal({ isEdit: true })}
      {showDeleteModal && <DeleteModal />}
    </div>
  );
}
